import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Only 'en' loads eagerly (it's the fallback and the default homepage
// language). The other 20 locale JSON files (~2.7MB combined) were
// previously all imported statically here, so every visitor's main.js
// bundle carried all 21 languages regardless of which one they needed.
// That bloated the entry bundle and delayed the first paint of translated
// text (LCP). Each locale is now its own webpack chunk, fetched on demand.
import en from '../locales/en.json';

const localeLoaders = {
  es: () => import('../locales/es.json'),
  de: () => import('../locales/de.json'),
  ja: () => import('../locales/ja.json'),
  fr: () => import('../locales/fr.json'),
  'pt-BR': () => import('../locales/pt-BR.json'),
  ru: () => import('../locales/ru.json'),
  it: () => import('../locales/it.json'),
  'zh-CN': () => import('../locales/zh-CN.json'),
  nl: () => import('../locales/nl.json'),
  pl: () => import('../locales/pl.json'),
  tr: () => import('../locales/tr.json'),
  ko: () => import('../locales/ko.json'),
  ar: () => import('../locales/ar.json'),
  vi: () => import('../locales/vi.json'),
  th: () => import('../locales/th.json'),
  sv: () => import('../locales/sv.json'),
  cs: () => import('../locales/cs.json'),
  el: () => import('../locales/el.json'),
  id: () => import('../locales/id.json'),
  hi: () => import('../locales/hi.json'),
};

// In-memory cache so switching back to an already-loaded language is instant.
const localeCache = { en };

async function loadLocale(code) {
  if (localeCache[code]) return localeCache[code];
  const loader = localeLoaders[code];
  if (!loader) return null;
  const mod = await loader();
  localeCache[code] = mod.default;
  return mod.default;
}

// RTL languages
const RTL_LANGUAGES = ['ar'];

// 21 languages with significant web traffic (W3Techs/Statista 2024 data)
export const LANGUAGES = [
  // Tier 1 - Top web languages
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'pt-BR', name: 'Portuguese', nativeName: 'Português', flag: '🇧🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
  { code: 'zh-CN', name: 'Chinese', nativeName: '简体中文', flag: '🇨🇳' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
  // Tier 2 - Important regional languages
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', rtl: true },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'id', name: 'Indonesian', nativeName: 'Indonesia', flag: '🇮🇩' },
];

const LanguageContext = createContext(undefined);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [translations, setTranslations] = useState(en);

  useEffect(() => {
    let cancelled = false;

    const applyLanguage = async (code) => {
      const data = await loadLocale(code);
      if (!cancelled && data) {
        setLanguage(code);
        setTranslations(data);
      }
    };

    // Check URL for language
    const pathLang = window.location.pathname.split('/')[1];
    const validLang = LANGUAGES.find(l => l.code === pathLang);

    if (validLang) {
      applyLanguage(validLang.code);
    } else {
      // Check localStorage
      const savedLang = localStorage.getItem('wheel-language');
      if (savedLang && (savedLang === 'en' || localeLoaders[savedLang])) {
        applyLanguage(savedLang);
      } else {
        // Detect browser language
        const browserLang = navigator.language.split('-')[0];
        const fullBrowserLang = navigator.language;

        if (fullBrowserLang === 'en' || localeLoaders[fullBrowserLang]) {
          applyLanguage(fullBrowserLang);
        } else if (browserLang === 'en' || localeLoaders[browserLang]) {
          applyLanguage(browserLang);
        }
      }
    }

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Update document direction for RTL languages
    const isRTL = RTL_LANGUAGES.includes(language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Save preference
    localStorage.setItem('wheel-language', language);
  }, [language]);

  const changeLanguage = useCallback(async (langCode) => {
    if (langCode !== 'en' && !localeLoaders[langCode]) return;

    const data = await loadLocale(langCode);
    if (!data) return;

    setLanguage(langCode);
    setTranslations(data);

    // Update URL
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    const currentLang = LANGUAGES.find(l => l.code === pathParts[0]);

    if (currentLang) {
      pathParts[0] = langCode;
    } else {
      pathParts.unshift(langCode);
    }

    const newPath = '/' + pathParts.join('/');
    window.history.pushState({}, '', newPath);
  }, []);

  const t = useCallback((key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        let fallback = en;
        for (const fk of keys) {
          if (fallback && typeof fallback === 'object' && fk in fallback) {
            fallback = fallback[fk];
          } else {
            return key; // Return key if not found
          }
        }
        value = fallback;
        break;
      }
    }
    
    if (typeof value !== 'string') {
      // Support returnObjects option for arrays/objects
      if (replacements.returnObjects && (Array.isArray(value) || typeof value === 'object')) {
        return value;
      }
      return key;
    }
    
    // Replace placeholders
    let result = value;
    for (const [placeholder, replacement] of Object.entries(replacements)) {
      result = result.replace(new RegExp(`{{${placeholder}}}`, 'g'), String(replacement));
    }
    
    return result;
  }, [translations]);

  const getCurrentLanguage = useCallback(() => {
    return LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  }, [language]);

  const isRTL = RTL_LANGUAGES.includes(language);

  return (
    <LanguageContext.Provider value={{
      language,
      translations,
      changeLanguage,
      t,
      getCurrentLanguage,
      isRTL,
      languages: LANGUAGES
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
